/**
 * Modified Tot Storybook System
 * Shared high-resolution PNG export engine.
 *
 * Uses html-to-image instead of html2canvas because
 * html2canvas was failing to paint board typography.
 */
(() => {
  'use strict';

  /*
   * This asset is included by multiple Storybook sections.
   * Without this guard, each section can register another
   * document click listener and one click can download twice.
   */
  if (window.__storybookExportEngineInitialized) {
    return;
  }

  window.__storybookExportEngineInitialized = true;

  const HTML_TO_IMAGE_CDN =
    'https://cdn.jsdelivr.net/npm/html-to-image@1.11.13/dist/html-to-image.js';

  let htmlToImagePromise;

  function loadHtmlToImage() {
    if (window.htmlToImage) {
      return Promise.resolve(window.htmlToImage);
    }

    if (htmlToImagePromise) {
      return htmlToImagePromise;
    }

    htmlToImagePromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        'script[data-storybook-html-to-image]'
      );

      if (existingScript) {
        if (window.htmlToImage) {
          resolve(window.htmlToImage);
          return;
        }

        existingScript.addEventListener(
          'load',
          () => {
            if (window.htmlToImage) {
              resolve(window.htmlToImage);
            } else {
              reject(
                new Error(
                  'html-to-image loaded but is unavailable.'
                )
              );
            }
          },
          { once: true }
        );

        existingScript.addEventListener(
          'error',
          () => {
            reject(
              new Error(
                'Unable to load the PNG export library.'
              )
            );
          },
          { once: true }
        );

        return;
      }

      const script = document.createElement('script');

      script.src = HTML_TO_IMAGE_CDN;
      script.async = true;
      script.dataset.storybookHtmlToImage = 'true';

      script.onload = () => {
        if (window.htmlToImage) {
          resolve(window.htmlToImage);
        } else {
          reject(
            new Error(
              'html-to-image loaded but is unavailable.'
            )
          );
        }
      };

      script.onerror = () => {
        reject(
          new Error(
            'Unable to load the PNG export library.'
          )
        );
      };

      document.head.appendChild(script);
    });

    return htmlToImagePromise;
  }

  function sanitizeFilename(value) {
    const cleaned = String(
      value || 'storybook-board'
    )
      .trim()
      .replace(/\.png$/i, '')
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');

    return cleaned || 'storybook-board';
  }

  async function waitForFonts() {
    if (!document.fonts?.ready) return;

    try {
      await document.fonts.ready;
    } catch (error) {
      console.warn(
        '[Storybook export] Font wait warning:',
        error
      );
    }
  }

  function waitForImages(root) {
    const images = Array.from(
      root.querySelectorAll('img')
    );

    return Promise.all(
      images.map((image) => {
        if (
          image.complete &&
          image.naturalWidth > 0
        ) {
          return image.decode
            ? image.decode().catch(() => {})
            : Promise.resolve();
        }

        return new Promise((resolve) => {
          const finish = () => resolve();

          image.addEventListener(
            'load',
            finish,
            { once: true }
          );

          image.addEventListener(
            'error',
            finish,
            { once: true }
          );
        });
      })
    );
  }

  function waitForLayout() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
  }

  function downloadDataUrl(dataUrl, filename) {
    const download =
      document.createElement('a');

    download.href = dataUrl;
    download.download = filename;

    document.body.appendChild(download);
    download.click();
    download.remove();
  }

  async function exportBoard(button) {
    /*
     * Prevent rapid double-clicks or duplicate event handling
     * from starting a second export for the same button.
     */
    if (button.dataset.exporting === 'true') {
      return;
    }

    button.dataset.exporting = 'true';

    const section = button.closest(
      '[data-storybook-section]'
    );

    const board = section?.querySelector(
      '[data-storybook-board]'
    );

    const status = section?.querySelector(
      '[data-storybook-export-status]'
    );

    const buttons =
      section?.querySelectorAll(
        '[data-storybook-export]'
      ) || [];

    if (!section || !board) {
      delete button.dataset.exporting;
      return;
    }

    const scale = Math.max(
      1,
      Number(button.dataset.scale || 2)
    );

    const currentWidth =
      board.getBoundingClientRect().width;

    const exportWidth = Math.max(
      600,
      Number(
        board.dataset.exportWidth ||
          currentWidth
      )
    );

    /*
     * Optional fixed export height.
     * When present, this forces paired boards to export
     * at identical page dimensions.
     */
    const requestedExportHeight = Number(
      board.dataset.exportHeight || 0
    );

    const filename = sanitizeFilename(
      board.dataset.exportFilename
    );

    const originalStyles = {
      width: board.style.width,
      maxWidth: board.style.maxWidth,
      margin: board.style.margin,
      transform: board.style.transform,
      boxShadow: board.style.boxShadow,
      height: board.style.height,
      minHeight: board.style.minHeight,
      maxHeight: board.style.maxHeight,
      overflow: board.style.overflow,
      display: board.style.display,
      flexDirection: board.style.flexDirection
    };

    /*
     * Pattern Story export-only layout.
     * Inline styles are used because html-to-image reliably captures
     * them when it creates the export clone.
     */
    const colorLayout = {
      footer: board.querySelector(
        '.sb-color-story__footer'
      )
    };

    const colorOriginalStyles = {
      footerFlex:
        colorLayout.footer?.style.flex || '',
      footerMarginTop:
        colorLayout.footer?.style.marginTop || ''
    };

    const patternLayout = {
      collectionHeader: board.querySelector(
        '.sb-pattern-story__collection-header'
      ),
      main: board.querySelector(
        '.sb-pattern-story__main'
      ),
      content: board.querySelector(
        '.sb-pattern-story__content'
      ),
      footer: board.querySelector(
        '.sb-pattern-story__footer'
      ),
      stripeGrid: board.querySelector(
        '.sb-pattern-story__pattern-grid--stripes'
      ),
      groups: Array.from(
        board.querySelectorAll(
          '.sb-pattern-story__group'
        )
      )
    };

    const patternOriginalStyles = {
      collectionHeaderPaddingBottom:
        patternLayout.collectionHeader?.style.paddingBottom || '',
      mainFlex:
        patternLayout.main?.style.flex || '',
      mainMinHeight:
        patternLayout.main?.style.minHeight || '',
      contentDisplay:
        patternLayout.content?.style.display || '',
      contentFlexDirection:
        patternLayout.content?.style.flexDirection || '',
      contentJustifyContent:
        patternLayout.content?.style.justifyContent || '',
      contentHeight:
        patternLayout.content?.style.height || '',
      footerFlex:
        patternLayout.footer?.style.flex || '',
      footerMarginTop:
        patternLayout.footer?.style.marginTop || '',
      stripeGridWidth:
        patternLayout.stripeGrid?.style.width || '',
      stripeGridMaxWidth:
        patternLayout.stripeGrid?.style.maxWidth || '',
      stripeGridMargin:
        patternLayout.stripeGrid?.style.margin || '',
      groupMargins:
        patternLayout.groups.map(
          (group) => group.style.marginTop
        )
    };

    buttons.forEach((item) => {
      item.disabled = true;
    });

    if (status) {
      status.textContent =
        `Preparing ${scale}× PNG…`;
    }

    try {
      const htmlToImage =
        await loadHtmlToImage();

      await waitForFonts();
      await waitForImages(board);

      board.classList.add(
        'storybook-board--exporting'
      );

      board.style.width =
        `${exportWidth}px`;

      board.style.maxWidth = 'none';
      board.style.margin = '0 auto';
      board.style.transform = 'none';
      board.style.boxShadow = 'none';

      if (requestedExportHeight > 0) {
        board.style.height =
          `${requestedExportHeight}px`;

        board.style.minHeight =
          `${requestedExportHeight}px`;

        board.style.maxHeight =
          `${requestedExportHeight}px`;

        board.style.overflow = 'hidden';
      } else {
        board.style.height = 'auto';
        board.style.minHeight = '0';
        board.style.maxHeight = 'none';
      }

      /*
       * Anchor the Color Story footer to the same bottom edge
       * as the Pattern Story when both use a fixed export height.
       */
      if (
        requestedExportHeight > 0 &&
        board.classList.contains(
          'sb-color-story__board'
        )
      ) {
        board.style.display = 'flex';
        board.style.flexDirection = 'column';

        if (colorLayout.footer) {
          colorLayout.footer.style.flex =
            '0 0 auto';
          colorLayout.footer.style.marginTop =
            'auto';
        }
      }

      /*
       * Make the Pattern Story fill its fixed page height:
       * start the swatches slightly higher, distribute the three
       * pattern groups through the available space, and anchor
       * the footer at the bottom.
       */
      if (
        requestedExportHeight > 0 &&
        board.classList.contains(
          'sb-pattern-story__board'
        )
      ) {
        board.style.display = 'flex';
        board.style.flexDirection = 'column';

        if (patternLayout.collectionHeader) {
          patternLayout.collectionHeader.style.paddingBottom =
            '8px';
        }

        if (patternLayout.main) {
          patternLayout.main.style.flex =
            '1 1 auto';
          patternLayout.main.style.minHeight =
            '0';
        }

        if (patternLayout.content) {
          patternLayout.content.style.display =
            'flex';
          patternLayout.content.style.flexDirection =
            'column';
          patternLayout.content.style.justifyContent =
            'space-between';
          patternLayout.content.style.height =
            '100%';
        }

        patternLayout.groups.forEach(
          (group, index) => {
            if (index > 0) {
              group.style.marginTop = '0';
            }
          }
        );

        if (patternLayout.stripeGrid) {
          patternLayout.stripeGrid.style.width =
            '88%';
          patternLayout.stripeGrid.style.maxWidth =
            '88%';
          patternLayout.stripeGrid.style.margin =
            '0 auto';
        }

        if (patternLayout.footer) {
          patternLayout.footer.style.flex =
            '0 0 auto';
          patternLayout.footer.style.marginTop =
            'auto';
        }
      }

      await waitForLayout();

      await new Promise((resolve) => {
        window.setTimeout(resolve, 500);
      });

      const rect =
        board.getBoundingClientRect();

      const exportHeight =
        requestedExportHeight > 0
          ? requestedExportHeight
          : Math.ceil(rect.height);

      let fontEmbedCSS = '';

      try {
        fontEmbedCSS =
          await htmlToImage.getFontEmbedCSS(
            board
          );
      } catch (error) {
        console.warn(
          '[Storybook export] Font embedding warning:',
          error
        );
      }

      const dataUrl =
        await htmlToImage.toPng(board, {
          cacheBust: true,
          backgroundColor: '#ffffff',

          width: exportWidth,
          height: exportHeight,

          /*
           * Use pixelRatio for the requested 2× / 3× export.
           * Do NOT also multiply canvasWidth/canvasHeight by scale:
           * html-to-image applies pixelRatio internally, so using both
           * can create an enormous canvas (effectively 4× / 9×) and
           * cause tall boards like Holly's Styling Board to fail.
           */
          pixelRatio: scale,

          fontEmbedCSS,

          skipAutoScale: true,

          filter: (node) => {
            if (!(node instanceof Element)) {
              return true;
            }

            return !(
              node.matches(
                '[data-storybook-export-controls]'
              ) ||
              node.matches(
                '.storybook-export-controls'
              ) ||
              node.matches(
                '.shopify-section-group-header-group'
              ) ||
              node.matches(
                '.shopify-section-group-footer-group'
              )
            );
          },

          style: {
            width: `${exportWidth}px`,
            maxWidth: 'none',
            margin: '0',
            transform: 'none',
            boxShadow: 'none',
            height:
              requestedExportHeight > 0
                ? `${requestedExportHeight}px`
                : 'auto',
            minHeight:
              requestedExportHeight > 0
                ? `${requestedExportHeight}px`
                : '0',
            maxHeight:
              requestedExportHeight > 0
                ? `${requestedExportHeight}px`
                : 'none',
            overflow:
              requestedExportHeight > 0
                ? 'hidden'
                : originalStyles.overflow
          }
        });

      downloadDataUrl(
        dataUrl,
        `${filename}-${scale}x.png`
      );

      if (status) {
        status.textContent =
          `${scale}× PNG exported.`;
      }
    } catch (error) {
      console.error(
        '[Storybook export]',
        error
      );

      if (status) {
        status.textContent =
          'Export failed. Open the browser console for details.';
      }
    } finally {
      board.classList.remove(
        'storybook-board--exporting'
      );

      board.style.width =
        originalStyles.width;

      board.style.maxWidth =
        originalStyles.maxWidth;

      board.style.margin =
        originalStyles.margin;

      board.style.transform =
        originalStyles.transform;

      board.style.boxShadow =
        originalStyles.boxShadow;

      board.style.height =
        originalStyles.height;

      board.style.minHeight =
        originalStyles.minHeight;

      board.style.maxHeight =
        originalStyles.maxHeight;

      board.style.overflow =
        originalStyles.overflow;

      board.style.display =
        originalStyles.display;

      board.style.flexDirection =
        originalStyles.flexDirection;

      if (colorLayout.footer) {
        colorLayout.footer.style.flex =
          colorOriginalStyles.footerFlex;
        colorLayout.footer.style.marginTop =
          colorOriginalStyles.footerMarginTop;
      }

      if (patternLayout.collectionHeader) {
        patternLayout.collectionHeader.style.paddingBottom =
          patternOriginalStyles.collectionHeaderPaddingBottom;
      }

      if (patternLayout.main) {
        patternLayout.main.style.flex =
          patternOriginalStyles.mainFlex;
        patternLayout.main.style.minHeight =
          patternOriginalStyles.mainMinHeight;
      }

      if (patternLayout.content) {
        patternLayout.content.style.display =
          patternOriginalStyles.contentDisplay;
        patternLayout.content.style.flexDirection =
          patternOriginalStyles.contentFlexDirection;
        patternLayout.content.style.justifyContent =
          patternOriginalStyles.contentJustifyContent;
        patternLayout.content.style.height =
          patternOriginalStyles.contentHeight;
      }

      patternLayout.groups.forEach(
        (group, index) => {
          group.style.marginTop =
            patternOriginalStyles.groupMargins[index] || '';
        }
      );

      if (patternLayout.stripeGrid) {
        patternLayout.stripeGrid.style.width =
          patternOriginalStyles.stripeGridWidth;
        patternLayout.stripeGrid.style.maxWidth =
          patternOriginalStyles.stripeGridMaxWidth;
        patternLayout.stripeGrid.style.margin =
          patternOriginalStyles.stripeGridMargin;
      }

      if (patternLayout.footer) {
        patternLayout.footer.style.flex =
          patternOriginalStyles.footerFlex;
        patternLayout.footer.style.marginTop =
          patternOriginalStyles.footerMarginTop;
      }

      buttons.forEach((item) => {
        item.disabled = false;
      });

      delete button.dataset.exporting;
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest(
        '[data-storybook-export]'
      );

      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      exportBoard(button);
    }
  );
})();
